import { useRef, type MouseEvent, type KeyboardEvent } from "react";
import { ChevronRight } from "lucide-react";
import CountUp from "./count-up";

type Tone = "up" | "down" | "neutral";

export interface StatCardProps {
  label: string;
  value: number;
  decimals?: number;
  suffix?: string;
  delta?: string;
  deltaTone?: Tone;
  spark?: number[];
  sparkColor?: string;
  highlight?: boolean;
  badge?: string;
  onClick?: () => void;
}

function sparkPoints(data: number[]) {
  if (data.length < 2) return "";
  const min = Math.min(...data), max = Math.max(...data), range = max - min || 1;
  return data
    .map((v, i) => `${((i / (data.length - 1)) * 100).toFixed(1)},${(25 - ((v - min) / range) * 21).toFixed(1)}`)
    .join(" ");
}

export default function StatCard({
  label, value, decimals = 0, suffix, delta, deltaTone = "up",
  spark, sparkColor = "#2d7a4d", highlight, badge, onClick,
}: StatCardProps) {
  const glowRef = useRef<HTMLSpanElement>(null);
  const clickable = !!onClick;

  const onMove = (e: MouseEvent<HTMLDivElement>) => {
    if (!glowRef.current) return;
    const r = e.currentTarget.getBoundingClientRect();
    glowRef.current.style.left = `${e.clientX - r.left}px`;
    glowRef.current.style.top = `${e.clientY - r.top}px`;
  };
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (onClick && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); onClick(); }
  };

  const toneCls = deltaTone === "down" ? "text-[#c0493b]" : deltaTone === "neutral" ? "text-[#8a9088]" : "text-[#2d7a4d]";
  const pts = spark ? sparkPoints(spark) : "";

  return (
    <div
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={onClick}
      onKeyDown={onKey}
      onMouseMove={highlight ? onMove : undefined}
      aria-label={clickable ? `${label}: ver detalhes` : undefined}
      className={[
        "group relative overflow-hidden rounded-2xl px-4 pt-4 pb-3 text-left transition-all duration-200",
        "hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(26,74,46,0.10)]",
        clickable ? "cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2d7a4d] focus-visible:ring-offset-2" : "",
        highlight ? "bg-gradient-to-br from-[#1a4a2e] to-[#2d7a4d] text-white" : "bg-white border border-[#ebe7dc]",
      ].join(" ")}
    >
      {highlight && (
        <span
          ref={glowRef}
          aria-hidden
          className="pointer-events-none absolute left-1/2 top-[40%] h-[220px] w-[220px] -translate-x-1/2 -translate-y-1/2 rounded-full opacity-0 transition-opacity duration-300 group-hover:opacity-100"
          style={{ background: "radial-gradient(circle, rgba(245,197,99,.35), transparent 65%)" }}
        />
      )}
      <div className="relative flex items-start justify-between gap-2">
        <span className={`text-[12.5px] font-medium ${highlight ? "text-white/80" : "text-[#8a9088]"}`}>{label}</span>
        {clickable && (
          <ChevronRight className={`h-4 w-4 shrink-0 transition-all duration-200 group-hover:translate-x-0.5 ${highlight ? "text-white/60" : "text-[#c5c9c3] group-hover:text-[#2d7a4d]"}`} />
        )}
      </div>
      <div className={`relative mt-1 text-[30px] font-bold leading-tight tracking-tight ${highlight ? "text-white" : "text-[#1f2621]"}`}>
        <CountUp value={value} decimals={decimals} />
        {suffix && <span className="ml-1 text-xl">{suffix}</span>}
      </div>
      {delta && !highlight && <div className={`relative text-xs font-semibold ${toneCls}`}>{delta}</div>}
      {badge && highlight && (
        <span className="relative mt-2 inline-flex items-center gap-1.5 rounded-full bg-white/20 px-2.5 py-0.5 text-[11px] font-bold">● {badge}</span>
      )}
      {pts && !highlight && (
        <svg className="agro-draw mt-2 block h-7 w-full" viewBox="0 0 100 28" preserveAspectRatio="none" aria-hidden>
          <polyline points={pts} fill="none" stroke={sparkColor} strokeWidth="2" vectorEffect="non-scaling-stroke" />
        </svg>
      )}
    </div>
  );
}
